import { gql } from '@apollo/client';
import { CartPrice, CART_PRICE_FRAGMENT } from '../useCart';

export type OrderCardTransactionDetails = {
  amountPaid: string;
  cardDetail: string;
  displayCard: string;
  lastFour: string;
  source: string;
  __typename: 'RestCardTransactionDetails';
};

export type ShippingStatus =
  | { reason: string; request_number: string; return_method: string }
  | null
  | string;

export type ItemStatus =
  | 'pending_authorization'
  | 'on_order'
  | 'shipped'
  | 'paid_in_full'
  | 'payment_pending'
  | 'bill_failure'
  | 'return_requested'
  | 'return_rejected';

export enum OrderItemStatusGroup {
  Open = 'Open',
  Shipped = 'Shipped',
  Returned = 'Returned',
  Refunded = 'Refunded',
  Cancelled = 'Cancelled'
}

export enum OrderItemReturnPolicy {
  FullRefund,
  CreditOnly,
  FinalSale,
  ExtendedHolidays,
  FullRefundMarkdown,
  CreditOnlyMarkdown
}

export type OrderItemAttributes = {
  __typename: 'RestOrderItemAttributes';
  color: string;
  customization: string | null;
  designerName: string;
  details: {
    __typename: 'RestOrderItemDetails';
    name: string;
  };
  excessShipping: string | null;
  excludedFromRewards: string | null;
  giftEligibility: string | null;
  images: {
    __typename: 'RestOrderItemImages';
    small: string;
    medium: string;
  };
  isReturnable: boolean;
  isRefundFailure: boolean;
  prices: {
    __typename: 'RestOrderItemPrices';
    depositPrice: CartPrice;
    retailPrice: CartPrice;
    totalPrice: CartPrice;
    totalBalancePayment: CartPrice;
  };
  productHref: string;
  promotionApplicableMessage: string | null;
  quantity: number;
  returnPolicy: OrderItemReturnPolicy;
  refundReason: string | null;
  refundReasonCode: number | null;
  shippingStatus: ShippingStatus;
  shipmentNumber: string;
  skuId: number;
  status: ItemStatus;
  statusGroup: OrderItemStatusGroup;
  shipmentTrackingUrl: string;
  size: string;
  variantId: number;
};

export type OrderItem = {
  __typename: 'RestOrderItem';
  id: string;
  attributes: OrderItemAttributes;
};

export type OrderTotals = {
  __typename: 'RestOrderTotals';
  shippingAndPackaging: string;
  orderSubtotal: string;
  salesTaxesAndDuties: string;
  orderTotal: {
    __typename: 'RestOrderTotal';
    price: string | number;
    currency: string;
  };
  remainingBalance: {
    __typename: 'RestOrderRemainingBalance';
    price: string | number;
    currency: string;
  };
};

export type OrderItemRelationship = {
  __typename: 'RestOrderItemRelationship';
  id: string;
};

export type OrderDiscounts = {
  __typename: 'RestOrderDiscounts';
  human: string;
  price: string;
  currency: string;
  currencyHtmlCode: string;
};

export type OrderPaymentSummary = {
  __typename: 'RestOrderPaymentSummary';
  giftCode: string | null;
  discounts: OrderDiscounts;
};

export type Order = {
  __typename: 'RestOrder';
  id: string;
  attributes: {
    __typename: 'RestOrderAttributes';
    cardTransactionDetails: OrderCardTransactionDetails[];
    totalAmountPaid: string;
    rewardsToBeEarned: {
      __typename: 'RestOrderRewards';
      totalPoints: number;
    };
    orderNumber: string;
    createdAt: string;
    currencyUnit: string;
    status: string;
    totals: OrderTotals;
    shipmentMethod: number;
    address: {
      __typename: 'RestOrderAddress';
      addressLine1: string;
      addressLine2: string;
      city: string;
      state: string;
      postalCode: string;
      countryCode: string;
      firstName: string;
      lastName: string;
      phone: string;
      source: string;
    };
    firstOrder: boolean;
    paymentSummary: OrderPaymentSummary;
    isAddressChangeable: boolean;
    isPaymentChangeable: boolean;
    hasBillFailure: boolean;
    shipmentNumber: string;
  };
  relationships: {
    __typename: 'RestOrderRelationships';
    orderItems: {
      __typename: 'RestOrderOrderItems';
      data: OrderItemRelationship[];
    };
  };
};

export type GetOrderQueryVariables = {
  orderNumber: string;
  countryCode: string;
};

export type GetOrderQuery = {
  getOrder: {
    __typename: 'RestOrderPayload';
    data: Order[];
    included: OrderItem[];
  };
};

export const ORDER_ITEM_FRAGMENT = gql`
  fragment OrderItemFragment on RestOrderItem {
    id
    attributes @type(name: "RestOrderItemAttributes") {
      color
      customization
      designerName: designer_name
      details @type(name: "RestOrderItemDetails") {
        name
      }
      excessShipping: excess_shipping
      excludedFromRewards: excluded_from_rewards
      giftEligibility: gift_eligibility
      images: primary_image_urls @type(name: "RestOrderItemImages") {
        small: small_url
        medium: medium_url
      }
      isReturnable: is_returnable
      isRefundFailure: is_refund_failure
      prices: formatted_prices @type(name: "RestOrderItemPrices") {
        depositPrice: deposit @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        retailPrice: retail_price @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        totalPrice: total_price @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
        totalBalancePayment: total_balance_payment @type(name: "RestCartPrice") {
          ...CartPriceFragment
        }
      }
      productHref: product_href
      promotionApplicableMessage: promotion_applicable_message
      quantity
      returnPolicy: return_policy
      refundReason: refund_reason
      refundReasonCode: refund_reason_code
      shippingStatus: shipping_status
      shipmentNumber: shipping_number
      skuId: sku_id
      status
      statusGroup: status_group
      shipmentTrackingUrl: shipment_tracking_url
      size
      variantId: variant_id
    }
  }
  ${CART_PRICE_FRAGMENT}
`;

export const ORDER_FRAGMENT = gql`
  fragment OrderFragment on RestOrder {
    id
    attributes @type(name: "RestOrderAttributes") {
      cardTransactionDetails: card_transaction_details @type(name: "RestCardTransactionDetails") {
        amountPaid: amt_paid
        cardDetail: card_detail
        displayCard: display_card
        lastFour: last_four
        source
      }
      totalAmountPaid: total_amount_paid
      rewardsToBeEarned: rewards_to_be_earned @type(name: "RestOrderRewards") {
        totalPoints: total_points
      }
      currencyUnit: currency_unit
      orderNumber: order_number
      createdAt: created_at
      status
      totals @type(name: "RestOrderTotals") {
        shippingAndPackaging: shipping_and_packaging
        orderSubtotal: order_subtotal
        salesTaxesAndDuties: sales_taxes_and_duties
        orderTotal: order_total @type(name: "RestOrderTotal") {
          price
          currency
        }
        remainingBalance: remaining_balance @type(name: "RestOrderRemainingBalance") {
          price
          currency
        }
      }
      shipmentMethod: shipment_method
      address @type(name: "RestOrderAddress") {
        firstName: first_name
        lastName: last_name
        addressLine1: address_line1
        addressLine2: address_line2
        phone
        city
        state
        postalCode: postal_code
        countryCode: country_code
        source
      }
      firstOrder: first_order
      paymentSummary: payment_summary @type(name: "RestOrderPaymentSummary") {
        giftCode: gift_code
        discounts @type(name: "RestOrderDiscounts") {
          human
          price
          currency
          currencyHtmlCode: currency_html_code
        }
      }
      isAddressChangeable: is_address_changeable
      isPaymentChangeable: is_payment_changeable
    }
    relationships @type(name: "RestOrderRelationships") {
      orderItems: order_items @type(name: "RestOrderOrderItems") {
        data @type(name: "RestOrderItemRelationship") {
          id
        }
      }
    }
  }
`;

export const GET_ORDER_QUERY = gql`
  query GetOrderQuery($orderNumber: string!, $countryCode: String!) {
    getOrder(orderNumber: $orderNumber, countryCode: $countryCode)
      @rest(
        type: "RestOrderPayload"
        endpoint: "cart"
        path: "/v3.5/orders?order_number={args.orderNumber}&include=order_items&country_code={args.countryCode}"
      ) {
      data @type(name: "RestOrder") {
        ...OrderFragment
      }
      included @type(name: "RestOrderItem") {
        ...OrderItemFragment
      }
    }
  }
  ${ORDER_FRAGMENT}
  ${ORDER_ITEM_FRAGMENT}
`;
